import ConsoleDriver from './logger/drivers/console';

const defaultOptions = {
  level: 0,
  drivers: [],
  context: {}
};

const Logger = {
  DEBUG: 0,
  INFO: 1,
  WARN: 2,
  ERROR: 3,

  _level: 1,
  _drivers: [],
  _context: {},

  /**
   * Adds a logging driver to the list of drivers
   * @param {object} driver
   */
  addDriver: function (driver) {
    this._drivers.push(driver);
    return this;
  },

  /**
   * Set the current logging context
   * @param {object} context
   */
  setContext: function (context) {
    this._context = context;
    return this;
  },

  /**
   * Retrieve the current logging context
   * @return {object}
   */
  getContext: function () {
    // return a copy of context so that outside code cannot modify context
    return this._context;
  },

  /**
   * Modify the logging context for the scope of a single callback function
   * @param  {Function} callback The callback to fire, passing the context as it's only parameter
   */
  withContext: function (callback) {
    const cache = Object.assign({}, this._context);
    callback(this._context);
    Object.assign(this._context, cache);
    return this;
  },

  /**
   * Set the current log level threshold - logs of a lower level than this will no longer be logged to any handlers
   * @param {number} level
   */
  setLevel: function (level) {
    this._level = level;
    return this;
  },

  /**
   * Create a logger instance
   * @param  {object} options Logger instance options
   * @return {object}
   */
  create: function (options) {
    options = Object.assign({
      level: this._level,
      context: Object.assign({}, this._context),
      drivers: this._drivers.slice(0)
    }, options);

    const logger = Object.create(this);

    logger._level = 1;
    logger._context = {};
    logger._drivers = [];

    logger.configure(options);

    return logger;
  },

  /**
   * Configure the current logger instance
   * @param  {object} options Logger instance options
   * @return {object}
   */
  configure: function (options) {
    options = Object.assign({}, defaultOptions, options);
    this.setLevel(options.level);
    this.setContext(options.context);

    options.drivers.forEach(function (driver) {
      this.addDriver(driver);
    }, this);

    return this;
  },

  /**
   * Logs a message to all available handlers
   * @param  {mixed}  message The message to be logged
   * @param  {number} level   The log level of the message
   * @return {object}
   */
  log: function (message, level = this.INFO) {
    // ignore logs with a lower level than the log level
    if (level < this._level) {
      return this;
    }

    this._drivers.forEach(function (driver) {
      driver.log(level, message, this);
    }, this);

    return this;
  },

  /**
   * Logs an error or exception to all available handlers
   * @param  {Error} error The error to be logged
   * @return {object}
   */
  logException: function (error) {
    this._drivers.forEach(function (driver) {
      driver.logException(error, this);
    }, this);
    return this;
  },

  /**
   * Wraps a callback in a try...catch and logs exceptions
   * @param  {Function} callback The callback to be wrapped
   * @param  {mixed}    context  The context to call the callback on
   * @return {Function}
   */
  wrap: function (callback, context = null) {
    const logger = this;
    return function () {
      try {
        return callback.apply(context, arguments);
      } catch (e) {
        logger.logException(e);
      }
    };
  },

  /**
   * Helper function for logging debug messages
   * @param  {mixed} message The message to be logged
   * @return {object}
   */
  debug: function (message) {
    return this.log(message, this.DEBUG);
  },

  /**
   * Helper function for logging info messages
   * @param  {mixed} message The message to be logged
   * @return {object}
   */
  info: function (message) {
    return this.log(message, this.INFO);
  },

  /**
   * Helper function for logging warning messages
   * @param  {mixed} message The message to be logged
   * @return {object}
   */
  warn: function (message) {
    return this.log(message, this.WARN);
  },

  /**
   * Helper function for logging error messages
   * @param  {mixed} message The message to be logged
   * @return {object}
   */
  error: function (message) {
    return this.log(message, this.ERROR);
  }
};

Logger.addDriver(ConsoleDriver);

export { Logger as default };
